// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Color utilities
// ---------------------------------------------------------------------------
// Used by ThemeContext to derive a readable foreground and contrast-aware
// hover/active shades from a host-supplied brand color. Everything else in the
// palette is derived in CSS (see styles/variables.css).
// ---------------------------------------------------------------------------

export type Rgb = [number, number, number];

/** Widget foregrounds, matching the light/dark text colors in variables.css. */
const DARK_FOREGROUND = '#00211b';
const LIGHT_FOREGROUND = '#ffffff';
const DARK_FOREGROUND_RGB: Rgb = [0, 33, 27];
const LIGHT_FOREGROUND_RGB: Rgb = [255, 255, 255];

/** Below this relative luminance a color has no perceptible darker shade. */
const LIGHTEN_BELOW_LUMINANCE = 0.15;

const HEX_RE = /^#([0-9a-f]{3,8})$/i;

function parseHex(color: string): Rgb | null {
  const digits = HEX_RE.exec(color.trim())?.[1];
  if (!digits || ![3, 4, 6, 8].includes(digits.length)) return null;

  const shorthand = digits.length <= 4;
  const channel = (index: number): number =>
    parseInt(
      shorthand ? digits.charAt(index).repeat(2) : digits.slice(index * 2, index * 2 + 2),
      16,
    );

  const rgb: Rgb = [channel(0), channel(1), channel(2)];
  return rgb.some(Number.isNaN) ? null : rgb;
}

/**
 * Resolve any CSS color to RGB, or `null` when the browser rejects it.
 * Hex is parsed directly; anything else round-trips through the CSSOM.
 */
export function toRgb(color: string): Rgb | null {
  const hex = parseHex(color);
  if (hex) return hex;
  if (typeof document === 'undefined') return null;

  const probe = document.createElement('span');
  probe.style.color = color;
  if (!probe.style.color) return null;

  probe.style.display = 'none';
  document.body.appendChild(probe);
  const computed = window.getComputedStyle(probe).color;
  probe.remove();

  const parts = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(computed);
  if (!parts) return null;
  return [Number(parts[1]), Number(parts[2]), Number(parts[3])];
}

function channelLuminance(value: number): number {
  const srgb = value / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance. */
function relativeLuminance([r, g, b]: Rgb): number {
  return (
    0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)
  );
}

function contrastRatio(a: number, b: number): number {
  const [lighter, darker] = a > b ? [a, b] : [b, a];
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick whichever widget foreground contrasts best against `color`.
 * Returns `null` when the color can't be parsed.
 */
export function readableForeground(color: string): string | null {
  const rgb = toRgb(color);
  if (!rgb) return null;

  const luminance = relativeLuminance(rgb);
  const againstDark = contrastRatio(luminance, relativeLuminance(DARK_FOREGROUND_RGB));
  const againstLight = contrastRatio(luminance, relativeLuminance(LIGHT_FOREGROUND_RGB));
  return againstDark >= againstLight ? DARK_FOREGROUND : LIGHT_FOREGROUND;
}

/**
 * Which end to mix toward for hover/active shades. Near-black brand colors
 * lighten, since they have no visible darker state.
 */
export function shadeDirection(color: string): 'black' | 'white' | null {
  const rgb = toRgb(color);
  if (!rgb) return null;
  return relativeLuminance(rgb) < LIGHTEN_BELOW_LUMINANCE ? 'white' : 'black';
}

// ---------------------------------------------------------------------------
// Dark-scheme adaptation
// ---------------------------------------------------------------------------
// Host colors describe the light scheme. In dark mode they are re-mapped onto
// fixed lightness bands, so the brand hue survives while the text/background
// pair stays legible whatever lightness the host supplied. Saturation is capped
// because fully saturated hues read as neon at these extremes.
// ---------------------------------------------------------------------------

const DARK_BACKGROUND_LIGHTNESS = 0.1;
const DARK_BACKGROUND_MAX_SATURATION = 0.3;
const DARK_TEXT_LIGHTNESS = 0.93;
const DARK_TEXT_MAX_SATURATION = 0.18;

/** Hue in degrees [0,360), saturation and lightness in [0,1]. */
type Hsl = [number, number, number];

function toHsl([r, g, b]: Rgb): Hsl {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) return [0, 0, lightness];

  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue: number;
  if (max === red) hue = (green - blue) / delta + (green < blue ? 6 : 0);
  else if (max === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;

  return [hue * 60, saturation, lightness];
}

function hueToChannel(p: number, q: number, t: number): number {
  let offset = t;
  if (offset < 0) offset += 1;
  if (offset > 1) offset -= 1;
  if (offset < 1 / 6) return p + (q - p) * 6 * offset;
  if (offset < 1 / 2) return q;
  if (offset < 2 / 3) return p + (q - p) * (2 / 3 - offset) * 6;
  return p;
}

function hslToHex(h: number, s: number, l: number): string {
  const hue = (((h % 360) + 360) % 360) / 360;

  let channels: Rgb;
  if (s === 0) {
    const value = Math.round(l * 255);
    channels = [value, value, value];
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    channels = [
      Math.round(hueToChannel(p, q, hue + 1 / 3) * 255),
      Math.round(hueToChannel(p, q, hue) * 255),
      Math.round(hueToChannel(p, q, hue - 1 / 3) * 255),
    ];
  }

  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Re-map a host color into the dark scheme, keeping its hue.
 * Returns `null` when the color can't be parsed.
 */
export function adaptToDark(color: string, role: 'background' | 'text'): string | null {
  const rgb = toRgb(color);
  if (!rgb) return null;

  const [hue, saturation] = toHsl(rgb);
  const isBackground = role === 'background';
  const lightness = isBackground ? DARK_BACKGROUND_LIGHTNESS : DARK_TEXT_LIGHTNESS;
  const maxSaturation = isBackground ? DARK_BACKGROUND_MAX_SATURATION : DARK_TEXT_MAX_SATURATION;

  return hslToHex(hue, Math.min(saturation, maxSaturation), lightness);
}
